import { Fragment, PropsWithChildren } from 'react'

import {
  AdditionalOption,
  BasicOption,
  OldAdditionalOption,
  OPTION_MAP,
  TRoomDetail,
} from '@/entities/room'
import { cn } from '@/shared/lib'
import { Button, Dialog, Separator } from '@/shared/ui'

import { Label, SubHeading } from '../common'

type Props = Pick<TRoomDetail, 'basicOptions' | 'missingOptions'> & {
  additionalOptions: (AdditionalOption | OldAdditionalOption)[]
}
export const OptionDialogTrigger = ({
  children,
  basicOptions,
  additionalOptions,
  missingOptions,
}: PropsWithChildren<Props>) => {
  return (
    <Dialog.Root>
      <Dialog.Trigger asChild>{children}</Dialog.Trigger>
      <OptionDialog
        basicOptions={basicOptions}
        additionalOptions={additionalOptions}
        missingOptions={missingOptions}
      />
    </Dialog.Root>
  )
}

type OptionList = {
  key: string
  title: string
  options: BasicOption[] | AdditionalOption[]
}

export const OptionDialog = ({
  basicOptions,
  additionalOptions,
  missingOptions,
}: Props) => {
  const hasBasicOptions = basicOptions.length > 0
  const hasAdditionalOptions = additionalOptions.length > 0
  const hasMissingOptions = missingOptions.length > 0

  const optionList = [
    hasBasicOptions && {
      key: 'basicOptions',
      title: '기본 옵션',
      options: basicOptions,
    },
    hasAdditionalOptions && {
      key: 'additionalOptions',
      title: '추가 옵션',
      options: additionalOptions,
    },
    hasMissingOptions && {
      key: 'missingOptions',
      title: '집에 없는 옵션',
      options: missingOptions,
    },
  ].filter(Boolean) as OptionList[]

  return (
    <Dialog.Content className="h-[760px] gap-0 p-0">
      <Dialog.Header className="p-6">
        <Dialog.Title>옵션 전체 보기</Dialog.Title>
      </Dialog.Header>
      <div className="scrollbar thick flex grow flex-col overflow-y-auto">
        {optionList.map(({ key, title, options }, index) => (
          <Fragment key={key}>
            <div className={cn('space-y-4 px-6')}>
              <SubHeading className="leading-6!">{title}</SubHeading>
              <OptionList
                layout="grid"
                options={options}
                disabled={key === 'missingOptions'}
              />
            </div>

            {index !== optionList.length - 1 && (
              <Separator color="light-gray" size="medium" className="my-6" />
            )}
          </Fragment>
        ))}
      </div>

      <Dialog.Footer className="p-6">
        <Dialog.Close asChild>
          <Button size="large" color="secondary">
            닫기
          </Button>
        </Dialog.Close>
      </Dialog.Footer>
    </Dialog.Content>
  )
}

type OptionListProps = {
  layout: 'grid' | 'flex'
  options: (AdditionalOption | OldAdditionalOption | BasicOption)[]
  disabled?: boolean
}

export const OptionList = ({ layout, options, disabled }: OptionListProps) => {
  return (
    <ul
      className={cn(
        'typo-body2 font-medium text-black',
        layout === 'grid' && 'grid grid-cols-3 gap-y-1',
        layout === 'flex' && 'flex flex-wrap gap-x-12.5 gap-y-1',
      )}
    >
      {options.map(option => (
        <li key={option} className="py-2.5">
          <Label
            size="small"
            icon={OPTION_MAP[option].icon}
            {...(disabled && {
              iconClassName: 'text-gray-300',
              className: 'text-gray-300 line-through',
            })}
          >
            {OPTION_MAP[option].label}
          </Label>
        </li>
      ))}
    </ul>
  )
}
